"use client";

import { Sidebar, useSidebar } from "@/components/ui/sidebar";
import { Perms, useBulkPermissions } from "@/hooks/use-permissions";
import { useSessionContext } from "@/contexts/session-context";
import { useEntitlements } from "@/app/providers/entitlements-provider";
import { useFeatureFlags } from "@/hooks/use-feature-flags";
import { ShopSummary } from "@/types/shop";
import { cn } from "@/utils/utils";
import {
  SidebarAdsIcon,
  SidebarAssetsIcon,
  SidebarChatIcon,
  SidebarExperiencesIcon,
  SidebarOpportunitiesIcon,
  SidebarProductsIcon,
  SidebarSmartLinksIcon,
} from "@/components/sidebar/sidebar-nav-icons";
import {
  FileSearchCorner,
  FlaskConical,
  BadgePercent,
  GitCompare,
  Home,
  Library,
  MessageCircle,
  Search,
  BadgeCheck,
  Telescope,
} from "lucide-react";
import Link from "next/link";
import * as React from "react";
import { NavMain } from "./nav-main";
import { NavUser } from "./nav-user";
import { StoreSwitcher } from "./store-switcher";
import { ShopifyReconnectCallout } from "./shopify-reconnect-callout";
import { SidebarToggleButton } from "./sidebar-toggle-button";
import { AppShellSidebar } from "./sidebar/app-shell-sidebar";
import type { SidebarNavData } from "./sidebar/nav-types";
import { getEditorHasUnsavedChanges } from "@/utils/editor-unsaved-guard";

export type { NavMainItem, NavSection } from "./sidebar/nav-types";

export const sideBarData: SidebarNavData = {
  sections: [
    {
      items: [
        {
          title: "Home",
          url: "/home",
          icon: Home,
          permissions: { metrics: ["read"] },
        },
        {
          title: "Agent",
          url: "/ai",
          icon: SidebarChatIcon,
        },
        {
          title: "Discover",
          url: "/inspiration",
          icon: Telescope,
          permissions: { lpInspiration: ["read"] },
          featureFlags: ["lpInspirationLibrary", "lpInspirationSidebar"],
        },
      ],
    },
    {
      title: "Strategy",
      items: [
        {
          title: "Opportunities",
          url: "/strategy/opportunities",
          icon: SidebarOpportunitiesIcon,
          permissions: { roadmap: ["read"] },
        },
      ],
    },
    {
      title: "Build",
      items: [
        {
          title: "Experiences",
          url: "/funnels",
          icon: SidebarExperiencesIcon,
        },
        {
          title: "Products",
          url: "/products",
          icon: SidebarProductsIcon,
        },
        {
          title: "Tests",
          url: "/experiments",
          icon: FlaskConical,
          permissions: { experiments: ["read"] },
        },
        {
          title: "Offers",
          url: "/offers",
          icon: BadgePercent,
          shopType: "SHOPIFY",
        },
        {
          title: "Smart Links",
          url: "/smart-links",
          icon: SidebarSmartLinksIcon,
        },
        {
          title: "Assets",
          url: "/media",
          icon: SidebarAssetsIcon,
        },
        {
          title: "Library",
          url: "/template-library",
          icon: Library,
        },
      ],
    },
    {
      title: "Channels",
      items: [
        {
          title: "Ads",
          url: "/ad-accounts",
          icon: SidebarAdsIcon,
          permissions: { sensitiveData: ["read"] },
        },
        {
          title: "Creators",
          url: "/creators",
          icon: BadgeCheck,
        },
      ],
    },
    {
      title: "AI Discovery",
      defaultOpen: false,
      items: [
        {
          title: "Overview",
          url: "/ai-discovery/overview",
          icon: Search,
          permissions: { aiDiscovery: ["read"] },
          entitlement: "aiDiscovery",
        },
        {
          title: "Prompts",
          url: "/ai-discovery/prompts",
          icon: MessageCircle,
          permissions: { aiDiscovery: ["read"] },
          entitlement: "aiDiscovery",
        },
        {
          title: "Competitors",
          url: "/ai-discovery/competitors",
          icon: GitCompare,
          permissions: { aiDiscovery: ["read"] },
          entitlement: "aiDiscovery",
        },
        {
          title: "Pages",
          url: "/ai-discovery/aeo-pages",
          icon: FileSearchCorner,
          permissions: { aiDiscovery: ["read"] },
          entitlement: "aiDiscovery",
        },
      ],
    },
  ],
};

interface Props extends React.ComponentProps<typeof Sidebar> {
  shop?: ShopSummary;
}

export const AppSidebar = ({ shop, ...props }: Props) => {
  const { authSessionData, isAdmin, canSwitchShop } = useSessionContext();
  const { isMobile, forceDrawer, setOpenMobile } = useSidebar();
  const entitlements = useEntitlements();
  const { data: featureFlags } = useFeatureFlags();
  const user = authSessionData.user;

  const allPermissions = React.useMemo(() => {
    return sideBarData.sections
      .flatMap((section) => section.items)
      .map((item) => item.permissions)
      .filter((perm): perm is Perms => perm != null);
  }, []);

  const { allowed: allowedMap } = useBulkPermissions(allPermissions);

  const filteredSections = sideBarData.sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        if (item.featureFlags?.some((flag) => featureFlags?.[flag] !== true)) {
          return false;
        }
        if (
          item.entitlement &&
          !entitlements.hasAdminAccess &&
          !entitlements[item.entitlement].hasAccess
        ) {
          return false;
        }
        return item.shopType ? shop?.shopType === item.shopType : true;
      }),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <AppShellSidebar
      {...props}
      id="app-sidebar"
      className={cn(props.className)}
      header={
        <>
          <div className="flex h-[var(--header-height)] w-full items-center justify-start gap-2 pl-1 md:justify-between">
            <Link
              href="/home"
              className="shrink-0"
              onClick={() => {
                if (isMobile && !getEditorHasUnsavedChanges()) {
                  setOpenMobile(false);
                }
              }}
            >
              <img
                src="/new-jurni-logo.svg"
                alt="Jurni"
                className="h-[19px] w-auto"
              />
            </Link>
            {!forceDrawer && (
              <SidebarToggleButton className="hidden md:inline-flex" />
            )}
          </div>
          <StoreSwitcher currentShop={shop} />
          {shop && <ShopifyReconnectCallout key={shop.id} shop={shop} />}
        </>
      }
      footer={
        <NavUser
          user={{
            name: user?.name || "",
            email: user?.email || "",
            avatar: user?.image || "",
            shop: shop?.name || "",
            shopAvatar: shop?.logo || "",
            isAdmin,
            canSwitchShop,
          }}
        />
      }
    >
      {filteredSections.map((section, index) => (
        <NavMain key={index} {...section} allowedMap={allowedMap} />
      ))}
    </AppShellSidebar>
  );
};
